import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages, SHIPPING_METHOD, SHIPPING_TIME } from '../data/mock';
import { Truck, Shield, Clock, Package, MapPin, Cpu } from 'lucide-react';

const ShippingPage = () => {
  useEffect(() => {
    document.title = 'Shipping Information | Motion Works Garage';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="">
        {/* Banner Section */}
        <section className="relative bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-50 border border-athletic-100 rounded-full text-athletic-700 text-xs font-semibold mb-4">
              <Truck className="w-3.5 h-3.5" />
              DELIVERY INFO
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-gray-900 font-extrabold mb-3">
              Shipping Information
            </h1>
            <p className="text-gray-600 max-w-lg">
              Fast, reliable shipping across the United States
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Shipping Highlights */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Truck className="w-7 h-7 text-athletic-700" />
              </div>
              <h3 className="font-display font-bold text-gray-900 text-lg mb-2">Free USA Shipping</h3>
              <p className="text-gray-500 text-sm">On every order, no minimum</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-athletic-700" />
              </div>
              <h3 className="font-display font-bold text-gray-900 text-lg mb-2">{SHIPPING_TIME}</h3>
              <p className="text-gray-500 text-sm">Standard USPS delivery time</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Package className="w-7 h-7 text-athletic-700" />
              </div>
              <h3 className="font-display font-bold text-gray-900 text-lg mb-2">Order Tracking</h3>
              <p className="text-gray-500 text-sm">Track your shipment</p>
            </div>
          </div>

          {/* Shipping Details */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 md:p-12">
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">Shipping Policy</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Shipping Rates</h3>
                <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-athletic-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Truck className="w-6 h-6 text-athletic-700" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-semibold">Free USPS Standard Shipping</p>
                    <p className="text-gray-600 text-sm">On every order — no minimum purchase required.</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Delivery Times</h3>
                <p className="text-gray-600 mb-4">
                  Standard shipping typically takes {SHIPPING_TIME} from the date of shipment. Please allow 1-2 business days for order processing before shipment.
                </p>
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>Orders are processed Monday through Friday</li>
                  <li>No deliveries on weekends or U.S. holidays</li>
                  <li>You'll receive a tracking number by email once your order ships</li>
                </ul>
              </div>

              <div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Shipping Carrier</h3>
                <p className="text-gray-600">
                  We ship all orders via {SHIPPING_METHOD} (United States Postal Service). You will receive a tracking number via email once your order ships.
                </p>
              </div>

              <div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Shipping Restrictions</h3>
                <p className="text-gray-600">
                  Currently, we only ship within the United States. We do not ship to P.O. boxes, APO/FPO addresses, or international destinations at this time.
                </p>
              </div>

              <div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Questions?</h3>
                <p className="text-gray-600 mb-4">
                  If you have any questions about shipping, please contact our support team:
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-athletic-500 text-white font-semibold rounded-xl hover:bg-athletic-400 transition-colors"
                  >
                    {CONTACT_INFO.email}
                  </a>
                  <a 
                    href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-900 font-semibold rounded-xl hover:bg-gray-200 transition-colors border border-gray-200"
                  >
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ShippingPage;
